<script setup lang="ts">
    import AllstarsLogin from '@/components/AllstarsLogin.ce.vue';
    import Appointment from '@/components/Appointment.ce.vue';
    import CallUs from '@/components/CallUs.ce.vue';
    import Chatbox from '@/components/Chatbox.ce.vue';
    import CustomTitle from '@/components/CustomTitle.ce.vue';
    import FullForm from '@/components/FullForm.ce.vue';
    import SideBar from '@/components/SideBar.ce.vue';
    import VenueHost from '@/components/VenueHost.ce.vue';
    import WelcomeScreen from '@/components/WelcomeScreen.ce.vue';
    import Menu from '@/components/menu/Menu.ce.vue';
    import PackageSelection from '@/components/packages/PackageSelection.ce.vue';
    import Quote from '@/components/quote/Quote.ce.vue';
    import { GOTO_SECTION, VENUE_HOST_EVENT, VENUE_HOST_RESET } from '@/constants/events';
    import { goToWebsite } from '@/helpers/footer';
    import { forceAncestorsToTop } from '@/helpers/scroll';
    import { type EventBusType } from '@/plugins/event-bus-plugin';
    import { type TargetSection } from '@/typings/widget';
    import axios from 'axios';
    import PathView from './PathView.ce.vue';

    const props = defineProps<{
        venue: string
        locale: string
        initialized: boolean
        maxGuestAmount?: number
        minGuestAmount?: number
        guestAmountStep?: number
        maxBudgetAmount?: number
        minBudgetAmount?: number
        budgetStep?: number
        isOpen?: boolean
        brandColor?: string
    }>();

    const { t } = useI18n();

    const eventBus = inject<EventBusType>('eventBus');
    const BG_IMG = `${import.meta.env.VITE_CDN_URL}/static/background_results.png`;

    // Stores
    const {
        venueId,
        venue: venueData,
        venueAgentId,
        venueEventTypes,
        venueWidgetParts,
        isPackageActive,
        isContactActive,
        isAppointmentActive,
    } = storeToRefs(useWidgetVenueStore());
    const { isMenuOpen, isQuoteOpen } = storeToRefs(useWidgetMenuStore());
    const {
        eventTypeFilter,
        guestAmountFilter,
        startDateFilter,
        startTimeFilter,
    } = storeToRefs(useWidgetFilterStore());

    // Refs
    const mainViewContainer: Ref<HTMLElement | null> = ref(null);
    const packagesContainer: Ref<HTMLElement | null> = ref(null);

    // Data
    const error: Ref<boolean> = ref(false);
    const pathView: Ref<boolean> = ref(false);
    const homeView: Ref<boolean> = ref(false);
    const comingFromMenu: Ref<boolean> = ref(false);
    const appointmentView: Ref<boolean> = ref(false);
    const fullFormView: Ref<boolean> = ref(false);
    const callUsView: Ref<boolean> = ref(false);
    const quoteView: Ref<boolean> = ref(false);
    const loginView: Ref<boolean> = ref(false);
    const chatboxView: Ref<boolean> = ref(false);

    // Methods
    const scrollToTop = ():void => {
        // Force the main-content container and every ancestor (host, body, ...) to the top so
        // the reset is complete on mobile too, where an outer container may be the real scroller.
        forceAncestorsToTop(mainViewContainer.value);
        requestAnimationFrame(() => { forceAncestorsToTop(mainViewContainer.value) });
    };

    const scrollToPackages = (): void => {
        if (packagesContainer.value && mainViewContainer.value) {
            const anchorOffsetTop = packagesContainer.value.getBoundingClientRect().top;
            const containerOffsetTop = mainViewContainer.value.getBoundingClientRect().top;

            // Calculate the scroll position relative to the scroll container
            const scrollPosition = mainViewContainer.value.scrollTop + anchorOffsetTop - containerOffsetTop;

            // Smooth scrolling
            mainViewContainer.value.scrollTo({
                top: scrollPosition,
                behavior: 'smooth',
            });
        }
    };

    // eslint-disable-next-line max-statements
    const fetchVenue = async(): Promise<void> => {
        /*
         * The component will already render even though the FAB is not clicked yet,
         * therefore we need to check if it's open before loading actual data.
         */
        if (!props.initialized) return;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        venueData.value = null;
        homeView.value = true;
        pathView.value = false;

        try {
            const response = await axios
                .post(
                    `${import.meta.env.VITE_API_BASE_URL}/api/v1/public/widget/venue/show`, {}, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': props.locale,
                            'X-Venue-Id': props.venue,
                        },
                    },
                );

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            venueData.value = response.data ?? null;
            venueWidgetParts.value = venueData.value?.widgetParts ?? [];
            venueAgentId.value = venueData.value?.widgetParts.find(part => part.widgetPartId === 7)?.value ?? '';
        } catch (err) {
            error.value = true;
            // eslint-disable-next-line no-console
            console.error('Backend call failed', err);
        }
    };

    const openPath = ():void => {
        homeView.value = false;
        pathView.value = true;
        chatboxView.value = false; // Hide chatbox when opening path
        scrollToTop();
    };

    const openQuote = ():void => {
        quoteView.value = true;
        scrollToTop();
    };

    const resetVenueHost = (): void => {
        eventBus?.emit(VENUE_HOST_RESET);
        eventBus?.emit(VENUE_HOST_EVENT, { step: 0 });
    };

    // eslint-disable-next-line max-statements
    const resetToMain = ():void => {
        homeView.value = true;
        error.value = false;
        pathView.value = false;
        comingFromMenu.value = false;
        appointmentView.value = false;
        fullFormView.value = false;
        callUsView.value = false;
        quoteView.value = false;
        loginView.value = false;
        chatboxView.value = false;
        eventTypeFilter.value = '';
        startTimeFilter.value = '';
        startDateFilter.value = '';
        guestAmountFilter.value = 0;
        resetVenueHost();
        scrollToTop();
    };

    const resetViews = (): void => {
        appointmentView.value = false;
        fullFormView.value = false;
        callUsView.value = false;
        quoteView.value = false;
        chatboxView.value = false;
        isMenuOpen.value = false;
        isQuoteOpen.value = false;
        loginView.value = false;
        scrollToTop();
    };

    // eslint-disable-next-line @typescript-eslint/require-await
    const openPackaqes = async(payload?: Record<string, unknown>): Promise<void> => {
        comingFromMenu.value = true;

        if (payload?.eventType && eventTypeFilter.value) {
            await nextTick();

            if (!venueEventTypes.value.length) await fetchVenue();

            const eventType = venueEventTypes.value.find(eventTypeItem => String(eventTypeItem.name)
                .toLowerCase() === String(payload.eventType)
                    .toLowerCase());

            // eslint-disable-next-line require-atomic-updates
            eventTypeFilter.value = String(eventType?.id ?? '');
        }
    };

    const handleSelectMenuOption = (value: string, payload?: Record<string, unknown>): void => {
        resetToMain();
        isMenuOpen.value = false;
        isQuoteOpen.value = false;

        switch (value) {
        case 'packages':
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            openPackaqes(payload);
            break;
        case 'journey':
            openPath();
            break;

        default:
            isQuoteOpen.value = true;
            openQuote();
            break;
        }
    };

    const onGotoSection = (config?: { target: TargetSection, payload?: Record<string, unknown> }): void => {
        if (!config?.target) return;

        resetViews();

        switch (config?.target) {
        case 'quote':
            handleSelectMenuOption('journey');
            break;
        case 'self_quote':
            handleSelectMenuOption('');
            break;
        case 'packages':
            handleSelectMenuOption('packages', config.payload);
            break;
        case 'appointment':
            appointmentView.value = true;
            break;
        case 'contact_form':
            fullFormView.value = true;
            break;
        default:
            break;
        }
    };

    // Watchers
    watch(() => props.locale, fetchVenue);
    watch(() => props.initialized, fetchVenue);
    watch(homeView, (nVal: boolean) => {
        if (nVal && props.isOpen) {
            resetVenueHost();
        }
    });

    watch(() => props.isOpen, nVal => {
        if (nVal) {
            resetViews();
            resetToMain();
        }
    });

    onMounted(() => {
        venueId.value = props.venue;
        eventBus?.on(GOTO_SECTION, onGotoSection);
    });
</script>

<template>
    <div class="main-view">
        <div class="main-view__container">
            <Menu
                v-if="isMenuOpen"
                :venue-manager="venueData?.venueManager"
                :brand-color="brandColor"
                @select-option="handleSelectMenuOption"
                @appointment-selected="resetViews(); appointmentView = true;"
                @contact-selected="resetViews(); fullFormView = true;"
                @call-selected="resetViews(); callUsView = true;"
            />
            <div
                v-else
                class="main-view__container__main"
            >
                <CustomTitle
                    :brand-color="brandColor"
                    @login="resetViews(); loginView = true;"
                />
                <div
                    id="main-content"
                    ref="mainViewContainer"
                    :style="{
                        backgroundImage: loginView ? `url(${BG_IMG})` : '',
                    }"
                    class="main-view__content"
                >
                    <Appointment
                        v-if="appointmentView && isAppointmentActive"
                        :locale="locale"
                        :venue="venue"
                        :venue-manager="venueData?.venueManager"
                        @back="resetToMain"
                        @scroll-to-top="scrollToTop"
                    />
                    <FullForm
                        v-else-if="fullFormView && isContactActive"
                        :venue="venue"
                        :locale="locale"
                        @back="resetToMain"
                        @scroll-to-top="scrollToTop"
                    />
                    <CallUs
                        v-else-if="callUsView"
                        :venue-data="venueData"
                        @back="resetToMain"
                        @scroll-to-top="scrollToTop"
                        @open-contact="resetViews(); fullFormView = true;"
                    />
                    <Quote
                        v-else-if="quoteView"
                        :venue="venue"
                        :locale="locale"
                        :max-guest-amount="maxGuestAmount"
                        :min-guest-amount="minGuestAmount"
                        :guest-amount-step="guestAmountStep"
                        @back-to-main="resetToMain"
                        @scroll-to-top="scrollToTop"
                        @call-selected="resetViews(); callUsView = true;"
                    />
                    <AllstarsLogin
                        v-else-if="loginView"
                        @back-to-main="resetToMain"
                    />
                    <Chatbox
                        v-else-if="chatboxView"
                        @back="resetToMain"
                    />
                    <template v-else>
                        <div class="main-host-target">
                            <div
                                id="main-host-target"
                                class="main-host-target__inner"
                            />
                        </div>
                        <VenueHost
                            v-if="isOpen"
                            :venue-manager="venueData?.venueManager"
                        />
                        <div
                            v-if="pathView && !homeView"
                            class="main-view__path-section"
                        >
                            <PathView
                                :max-guest-amount="maxGuestAmount"
                                :min-guest-amount="minGuestAmount"
                                :guest-amount-step="guestAmountStep"
                                :max-budget-amount="maxBudgetAmount"
                                :min-budget-amount="minBudgetAmount"
                                :budget-step="budgetStep"
                                :locale="locale"
                                :error="error"
                                :venue="venue"
                                :brand-color="brandColor"
                                @back-to-main="resetToMain"
                                @call-selected="resetViews(); callUsView = true;"
                                @scroll-to-top="scrollToTop"
                            />
                        </div>
                        <div
                            v-else
                            class="main-view__overview-section"
                        >
                            <WelcomeScreen
                                v-if="homeView && isOpen && !comingFromMenu"
                                :venue-event-types="venueEventTypes"
                                :brand-color="brandColor"
                                @go-to-path="openPath"
                                @go-to-packages="scrollToPackages"
                                @go-to-contact="resetViews(); fullFormView = true;"
                                @go-to-appointment="resetViews(); appointmentView = true;"
                                @go-to-chatbox="resetViews(); chatboxView = true;"
                            />
                            <div
                                v-if="isPackageActive && !pathView"
                                ref="packagesContainer"
                                class="main-view__packages"
                            >
                                <PackageSelection
                                    :locale="locale"
                                    :venue="venue"
                                    :error="error"
                                    :coming-from-menu="comingFromMenu"
                                    :is-open="isOpen"
                                    :max-guest-amount="maxGuestAmount"
                                    :min-guest-amount="minGuestAmount"
                                    :guest-amount-step="guestAmountStep"
                                    @scroll-to-top="scrollToTop"
                                    @toggle-home="homeView = $event"
                                    @back-to-main="resetToMain"
                                />
                            </div>
                        </div>
                        <div
                            v-if="homeView"
                            class="main-view__footer"
                            @click="goToWebsite"
                        >
                            {{ t('widget.global.made-constell') }}
                        </div>
                    </template>
                </div>
                <div class="main-view__footer-shadow" />
            </div>
            <SideBar
                :brand-color="brandColor"
                :in-appointment="appointmentView"
                :in-contact="fullFormView"
                :in-call="callUsView"
                @appointment-selected="resetViews(); appointmentView = true;"
                @contact-selected="resetViews(); fullFormView = true;"
                @call-selected="resetViews(); callUsView = true;"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.main-view {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: var(--border-radius-xs);
    block-size: 100%;
    transition: background-color 300ms linear;

    .main-host-target {
        position: absolute;
        z-index: var(--zindex-stack-thrice);
        overflow: hidden;
        block-size: 100%;
        inline-size: 100%;
        inset-block: 0 0;
        inset-inline: 0 0;
        pointer-events: none;

        &__inner {
            position: relative;
            overflow: hidden;
            block-size: 100%;
            inline-size: 100%;
        }
    }

    &__container {
        display: flex;
        overflow: hidden;
        justify-content: space-between;
        block-size: 100%;

        &__main {
            flex: 2;
            background-color: var(--background-secondary);
            block-size: 100%;
            inline-size: 100%;

            // Allow the flex item to shrink below its content's intrinsic width so wide
            // children (e.g. the product shop grid) don't overflow and push the SideBar off-screen
            min-inline-size: 0;
            transform: scale(1);
        }
    }

    &__content {
        position: relative;
        z-index: var(--zindex-stack-twice);
        block-size: calc(100% - var(--widget-header-height-mobile));
        -ms-overflow-style: none; /* IE & Edge */
        overflow-y: auto;
        scrollbar-width: none; /* Firefox */

        @include min-width(md) {
            block-size: calc(100% - var(--widget-header-height-tablet));
        }

        @include min-width(lg) {
            block-size: calc(100% - var(--widget-header-height));
        }

        &::-webkit-scrollbar {
            display: none; /* Chrome, Safari */
        }
    }

    &__overview-section,
    &__path-section {
        display: flex;
        flex-direction: column;
    }

    &__path-section {
        position: relative;
        overflow: hidden;
        block-size: 100%;
        min-block-size: 100%;
    }

    &__footer-shadow {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        background: linear-gradient(0deg, rgb(0 0 0 / 0.30) 0%, rgb(239 239 239 / 0) 100%);
        block-size: var(--size-10);
        inline-size: 100%;
        inset-block-end: 0;
        inset-inline-start: 0;
        pointer-events: none;
    }

    &__footer {
        padding: var(--spacing-02);
        margin: var(--spacing-02);
        color: var(--text-tertiary);
        cursor: pointer;
        font-size: var(--font-regular);
        letter-spacing: 0.5px;
        text-align: center;
        transition: color 250ms ease-in-out;

        @include min-width(md) {
            padding: var(--spacing-04);
            margin: var(--spacing-04);
        }

        &:hover {
            color: var(--text-secondary);
        }
    }

    @include min-width(md) {
        border-radius: var(--border-radius-md);
    }
}

</style>
